import {
  Button,
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormPrefixedInput,
  FormMessage,
  ToggleGroup,
  ToggleGroupItem,
  DataTable,
  V2Loader,
  cn,
  FormDescription,
} from "web-design-system";
import { useSIPCalculator } from "creso-core/hooks";
import { Fund } from "creso-core/types";
import { useMemo } from "react";
import { Area, AreaChart, XAxis } from "recharts";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import get from "lodash/get";
import { convertToCurrency } from "creso-core/utils";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import type { FundCompareBacktestOptions } from "@funds/utils/fund-compare-search-params";
import { getFundsDateOverlapRange } from "@funds/utils/funds-date-overlap";
import { BacktestCell } from "./backtest-cell";
import { sipCalculatorRowConfig } from "./config";

const columnHelper = createColumnHelper<{
  label: string;
  [key: string]: string | number;
}>();

const colors = [
  "var(--chart-1-300)",
  "var(--chart-2-300)",
  "var(--chart-3-300)",
  "var(--chart-4-300)",
];

export function HistoricalNavChart({
  funds,
  labelWidth,
  fundIds,
  variant = "default",
  backtestOptions,
  onBacktestOptionsCommit,
}: {
  funds: Fund[];
  fundIds: string[];
  labelWidth: number;
  variant?: "default" | "report";
  backtestOptions: FundCompareBacktestOptions;
  onBacktestOptionsCommit?: (options: FundCompareBacktestOptions) => void;
}) {
  const options = backtestOptions;

  const allSettled = useMemo(() => {
    return fundIds.length === funds.length;
  }, [fundIds, funds]);

  const { startDate, endDate } = useMemo(
    () => getFundsDateOverlapRange(funds, options.timePeriod),
    [funds, options.timePeriod]
  );

  const { data } = useSIPCalculator(
    {
      fundIds,
      startDate,
      endDate,
      sipFrequency: "Monthly",
      sipAmount: Number(options.subsequentSIP),
      lumpsumAmount: Number(options.initialInvestment),
    },
    {
      enabled: allSettled,
    }
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("label", {
        header: "",
        meta: {
          className: "bg-muted px-5 py-3",
        },
        size: labelWidth,
        maxSize: labelWidth,
      }),
      ...funds.map((fund, index) =>
        columnHelper.accessor(fund.fund_id, {
          header: "",
          meta: {
            className: `bg-background text-left px-5 py-3 text-sm ${
              index === funds.length - 1 ? "" : "border-r border-border"
            }`,
          },
          cell: ({ row }) => {
            const fundValue = data[index] || {};
            const series = Object.values(fundValue.data ?? {});
            const latestIncrementalData = series[series.length - 1];
            const result = {
              total_invested: latestIncrementalData?.total_invested,
              portfolio_value: latestIncrementalData?.portfolio_value,
              cagr: fundValue.cagr,
              standard_deviation: fundValue.standard_deviation,
              max_drawdown: fundValue.max_drawdown,
            };
            return <BacktestCell label={row.original.label} data={result} />;
          },
          size: Number.MAX_SAFE_INTEGER,
        })
      ),
      ...(variant === "default"
        ? [
            columnHelper.accessor("space", {
              header: "",
              size: 80,
              meta: {
                className: "bg-background",
              },
            }),
          ]
        : []),
    ],
    [funds, labelWidth, data, variant]
  );

  const tableData = useMemo(() => {
    return sipCalculatorRowConfig.map((config) => {
      const values = funds.reduce(
        (acc, fund, index) => {
          acc[fund.fund_id] = get(data[index], config.accessor as string) as {
            date: string;
            total_invested: number;
            portfolio_value: number;
          };
          return acc;
        },
        {} as Record<
          Fund["fund_id"],
          {
            date: string;
            total_invested: number;
            portfolio_value: number;
          }
        >
      );
      return {
        label: config.label,
        ...values,
      } as { label: string; [key: Fund["fund_id"]]: string };
    });
  }, [funds, data]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  const formatXAxis = (date: string) => {
    try {
      return format(new Date(date), "MMM yy");
    } catch {
      return "";
    }
  };

  const chartConfig = useMemo(() => {
    return funds.reduce(
      (acc, f, index) => ({
        ...acc,
        [f.legal_name]: {
          label: f.legal_name,
          color: colors[index],
        },
      }),
      {} satisfies ChartConfig
    );
  }, [funds]);

  const dates = useMemo(() => {
    return Array.from(
      new Set(data?.flatMap((item) => Object.keys(item.data ?? {})))
    ).sort();
  }, [data]);

  const series = useMemo(() => {
    return dates.map((date) => {
      const fundsValues = funds.reduce(
        (acc, fund, idx) => {
          acc[fund.legal_name] = data[idx]?.data?.[date]?.portfolio_value;
          return acc;
        },
        {} as Record<string, number | undefined>
      );
      return {
        date,
        ...fundsValues,
      };
    });
  }, [dates, data, funds]);

  const form = useForm<FundCompareBacktestOptions>({
    values: options,
  });

  const { isDirty } = form.formState;

  return (
    <div className="flex flex-col break-inside-avoid-page">
      <Collapsible
        defaultOpen
        className="w-full bg-muted rounded-lg border border-border group"
      >
        <CollapsibleTrigger
          className={cn(
            "flex w-full justify-between items-center px-5 py-4",
            variant === "report" && "pointer-events-none"
          )}
        >
          <span className="font-semibold text-base">Returns backtest</span>
          {variant === "default" ? (
            <ChevronDown className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180 text-muted-foreground" />
          ) : null}
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col">
          <ConditionalRenderer condition={variant === "default"}>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((data) =>
                  onBacktestOptionsCommit?.(data)
                )}
                className="flex flex-row items-start bg-muted gap-3 px-5 py-4"
              >
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="initialInvestment"
                  render={({ field }) => (
                    <FormPrefixedInput
                      prefix=""
                      type="number"
                      label="Initial investment amount"
                      {...field}
                    >
                      ₹
                    </FormPrefixedInput>
                  )}
                />
                <FormField
                  className="flex-1"
                  control={form.control}
                  name="subsequentSIP"
                  render={({ field }) => (
                    <FormPrefixedInput
                      prefix=""
                      type="number"
                      label="Subsequent SIP amount"
                      {...field}
                    >
                      ₹
                    </FormPrefixedInput>
                  )}
                />
                <FormField
                  control={form.control}
                  className="flex-1"
                  name="timePeriod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time period</FormLabel>
                      <FormControl>
                        <ToggleGroup
                          type="single"
                          value={field.value}
                          onValueChange={field.onChange}
                          className="p-0 w-fit gap-2"
                        >
                          <ToggleGroupItem
                            variant="outline"
                            className="bg-background data-[state=on]:bg-secondary px-4 py-2"
                            value="1 yr"
                          >
                            1 yr
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            variant="outline"
                            className="bg-background data-[state=on]:bg-secondary px-4 py-2"
                            value="3 yr"
                          >
                            3 yr
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            variant="outline"
                            className="bg-background data-[state=on]:bg-secondary px-4 py-2"
                            value="5 yr"
                          >
                            5 yr
                          </ToggleGroupItem>
                          <ToggleGroupItem
                            variant="outline"
                            className="bg-background data-[state=on]:bg-secondary px-4 py-2"
                            value="10 yr"
                          >
                            10 yr
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </FormControl>
                      <FormDescription>
                        Date overlap -{" "}
                        {format(new Date(startDate), "MMM dd, yyyy")} to{" "}
                        {format(new Date(endDate), "MMM dd, yyyy")}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  className="mt-5"
                  type="submit"
                  disabled={!isDirty || !onBacktestOptionsCommit}
                >
                  Update
                </Button>
              </form>
            </Form>
          </ConditionalRenderer>
          <V2Loader isLoading={!allSettled} className="size-full">
            {data.length > 0 && (
              <div className="flex flex-row py-5">
                <div
                  className="flex flex-col gap-1 border-r items-start border-border px-5 flex-shrink-0"
                  style={{ width: labelWidth }}
                >
                  <span className="text-xs text-muted-foreground">
                    Total investment
                  </span>
                  <span className="text-lg font-medium">
                    {convertToCurrency(
                      data[0]?.data?.[dates[dates.length - 1]]
                        ?.total_invested ?? 0,
                      0
                    )}
                  </span>
                </div>
                {funds.map((f, index) => {
                  if (!data[index].data?.[dates[dates.length - 1]]) {
                    return null;
                  }
                  const returns =
                    (data[index].data?.[dates[dates.length - 1]]
                      ?.portfolio_value ?? 0) -
                    (data[index].data?.[dates[dates.length - 1]]
                      ?.total_invested ?? 0);
                  const returnsPercentage =
                    (returns /
                      (data[index].data?.[dates[dates.length - 1]]
                        ?.total_invested ?? 0)) *
                    100;
                  return (
                    <div
                      key={f.fund_id}
                      className={cn(
                        "flex-1 flex flex-col gap-1 border-r overflow-hidden border-border px-4 items-center",
                        index === funds.length - 1 && "border-r-0"
                      )}
                    >
                      <span className="text-xs w-full text-left text-muted-foreground truncate">
                        {f.legal_name}
                      </span>
                      <div className="text-lg w-full justify-start font-medium flex items-baseline gap-1">
                        {convertToCurrency(
                          data[index].data?.[dates[dates.length - 1]]
                            ?.portfolio_value ?? 0,
                          0
                        )}
                        <span
                          data-sign={
                            returns > 0 ? "+" : returns === 0 ? "" : "-"
                          }
                          className="text-xs font-normal data-[sign='-']:text-destructive data-[sign='+']:text-constructive"
                        >
                          {convertToCurrency(returns, 0)} (
                          {returnsPercentage.toFixed(2)}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
                {variant === "default" && (
                  <div className="h-full w-[80px] bg-background"></div>
                )}
              </div>
            )}
            <ChartContainer
              className={cn(
                "aspect-auto px-5 w-full relative h-[400px]",
                variant === "report" && "h-70"
              )}
              config={chartConfig}
            >
              <AreaChart data={series}>
                <XAxis
                  domain={["dataMin", "dataMax"]}
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatXAxis}
                  interval="preserveStartEnd"
                  minTickGap={50}
                  tickCount={6}
                />
                {variant !== "report" && (
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        valueFormatter={(value) =>
                          convertToCurrency(value as number, 0)
                        }
                        labelFormatter={(label) => {
                          const date = Date.parse(label);
                          if (Number.isNaN(date)) {
                            return "";
                          }
                          return format(date, "MMM dd, yyyy");
                        }}
                        indicator="dot"
                      />
                    }
                  />
                )}
                {funds.map((f, index) => (
                  <defs key={`def-${f.fund_id}`}>
                    <linearGradient
                      z={1}
                      to="right"
                      id={`navGradient-${f.fund_id}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={colors[index]}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="95%"
                        stopColor={colors[index]}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                ))}
                {funds.map((f, index) => (
                  <Area
                    isAnimationActive={variant !== "report"}
                    connectNulls
                    key={`area-${f.fund_id}`}
                    dataKey={f.legal_name}
                    type="monotone"
                    stroke={colors[index]}
                    fill={`url(#navGradient-${f.fund_id})`}
                    strokeWidth={2}
                  />
                ))}
                <ChartLegend
                  className="w-full"
                  content={<ChartLegendContent />}
                />
              </AreaChart>
            </ChartContainer>
            <DataTable
              noHeader
              className="border-none mt-5 [&_table]:table-fixed"
              table={table}
              pagination={false}
            />
          </V2Loader>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
